/**
 * Tier filter component for PinnedClientsList
 * Handles tier statistics calculation and filter button rendering
 */

import React from 'react';
import { getTierColor } from '../../../services/TierColorService';
import '../../../styles/components/clients/pinned-list/PinnedListTierFilters.css';

/**
 * PinnedListTierFilters component
 * @param {Object} props - Component props
 * @param {Array} props.pinnedClients - Array of all pinned clients
 * @param {Set} props.activeTierFilters - Set of active tier filters
 * @param {Function} props.onToggleTierFilter - Function to toggle tier filter
 * @returns {JSX.Element|null} Tier filters component or null if not needed
 */
const PinnedListTierFilters = ({ 
  pinnedClients, 
  activeTierFilters, 
  onToggleTierFilter 
}) => {
  
  /**
   * Calculate pinned clients tier statistics for all portfolio types
   * @returns {Object|null} Tier statistics object or null if no clients
   */
  const calculatePinnedClientsTierStats = () => {
    if (pinnedClients.length === 0) return null;
    
    const tierCounts = {};
    pinnedClients.forEach(client => {
      const tier = client['Tier'] || 'Unknown';
      tierCounts[tier] = (tierCounts[tier] || 0) + 1;
    });
    
    const sortedTiers = Object.keys(tierCounts).sort((a, b) => {
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.localeCompare(b);
    });
    
    return { tierCounts, sortedTiers, totalClients: pinnedClients.length };
  };

  const pinnedTierStats = calculatePinnedClientsTierStats();
  
  // Don't render if no clients or only one tier type
  if (!pinnedTierStats || pinnedTierStats.sortedTiers.length <= 1) {
    return null;
  }

  /**
   * Format tier label for display
   * @param {string} tier - Tier value
   * @returns {string} Formatted tier label
   */
  const formatTierLabel = (tier) => {
    if (tier === 'Unknown') return 'unknown';
    if (tier === 'VIP') return 'vip';
    if (tier === 'N/A') return 'n/a';
    if (tier === 'n/a') return 'n/a';
    return `t${tier.toLowerCase()}`;
  };

  return (
    <div className="pinned-clients-list__tier-stats">
      {pinnedTierStats.sortedTiers.map((tier) => {
        const tierColor = getTierColor(tier, 'dark');
        const isActive = activeTierFilters.has(tier);
        
        return (
          <button 
            key={tier}
            className={`pinned-clients-list__tier-label ${isActive ? 'pinned-clients-list__tier-label--active' : ''}`}
            onClick={() => onToggleTierFilter(tier)}
            title={`Click to filter by Tier ${tier}${isActive ? ' (active)' : ''}`}
            style={{
              backgroundColor: isActive ? tierColor : 'white',
              borderColor: tierColor,
              color: isActive ? 'white' : tierColor,
              fontWeight: 500
            }}
          >
            {formatTierLabel(tier)}
          </button>
        );
      })}
    </div>
  );
};

export default PinnedListTierFilters;
